import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { STAKKO_PLAN_LABELS } from '../config';
import { useStakko } from '../context/StakkoProvider';
import { STAKKO_EVENTS, stakkoBus } from '../bus';
import type { StakkoCreditPack, StakkoPanelView } from '../types';
import {
  BellIcon,
  CheckIcon,
  ChevronRightIcon,
  CoinsIcon,
  GridIcon,
  LogOutIcon,
  SettingsIcon,
  SparklesIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from './icons';
import { StakkoPanelContent, panelTitle } from './panels';
import { StakkoAvatar, StakkoBadge } from './primitives';
import { StakkoRail } from './StakkoRail';
import type { StakkoPrimaryAction } from './StakkoRail';

export interface StakkoShellProps {
  children: ReactNode;
  /** Skryje rail (např. na marketingové landing page). */
  hideRail?: boolean;
  /** Volitelné primární akční tlačítko v railu (např. „Nový rozvrh“). */
  primaryAction?: StakkoPrimaryAction;
  /** Napojení na billing; bez něj se otevře účetní stránka. */
  onBuyCredits?: (pack: StakkoCreditPack) => void;
  className?: string;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = () => setMatches(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

function MobileMenu({
  onOpen,
}: {
  onOpen: (view: StakkoPanelView) => void;
}) {
  const { user, profile, entitlements, wallet, friends, incomingRequests, unreadNotifications, signOut } =
    useStakko();

  const name = profile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'Student';

  const tiles: Array<{ view: StakkoPanelView; label: string; icon: ReactNode; badge?: number }> = [
    { view: 'apps', label: 'Aplikace', icon: <GridIcon size={22} /> },
    { view: 'credits', label: 'Kredity', icon: <CoinsIcon size={22} />, badge: wallet.balance },
    { view: 'plans', label: 'Tarify', icon: <SparklesIcon size={22} /> },
    {
      view: 'friends',
      label: 'Přátelé',
      icon: <UsersIcon size={22} />,
      badge: incomingRequests.length || undefined,
    },
    { view: 'notifications', label: 'Oznámení', icon: <BellIcon size={22} />, badge: unreadNotifications || undefined },
    { view: 'todos', label: 'Úkoly', icon: <CheckIcon size={22} /> },
    { view: 'account', label: 'Účet', icon: <UserIcon size={22} /> },
    { view: 'settings', label: 'Nastavení', icon: <SettingsIcon size={22} /> },
  ];

  return (
    <div className="stakko-mobile-menu">
      {user ? (
        <div className="stakko-mobile-account">
          <StakkoAvatar name={name} photoURL={profile?.photoURL || user.photoURL} size={44} />
          <div>
            <strong>{name}</strong>
            <span>{user.email}</span>
          </div>
          <StakkoBadge tone={entitlements.plan === 'free' ? 'muted' : 'brand'}>
            {STAKKO_PLAN_LABELS[entitlements.plan]}
          </StakkoBadge>
        </div>
      ) : (
        <button type="button" className="stakko-btn stakko-btn-primary" onClick={() => onOpen('auth')}>
          Přihlásit se do Stakko
        </button>
      )}

      <div className="stakko-mobile-tiles">
        {tiles.map((tile) => (
          <button key={tile.view} type="button" onClick={() => onOpen(tile.view)}>
            <span className="stakko-mobile-tile-icon">{tile.icon}</span>
            <span>{tile.label}</span>
            {tile.badge && tile.badge > 0 ? (
              <span className="stakko-mobile-tile-badge">
                {tile.badge > 99 ? '99+' : tile.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="stakko-mobile-footer">
        <span>{friends.length} přátel · {wallet.balance} kreditů</span>
        {user ? (
          <button type="button" className="stakko-link" onClick={() => void signOut()}>
            <LogOutIcon size={15} /> Odhlásit
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function StakkoShell({
  children,
  hideRail = false,
  primaryAction,
  onBuyCredits,
  className,
}: StakkoShellProps) {
  const { user, profile, theme, unreadNotifications, incomingRequests } = useStakko();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  const resolvedTheme = theme === 'auto' ? (systemDark ? 'dark' : 'light') : theme;

  const [view, setView] = useState<StakkoPanelView | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (hideRail || !isDesktop) {
      document.body.classList.remove('stakko-rail-on');
      return;
    }
    document.body.classList.add('stakko-rail-on');
    return () => document.body.classList.remove('stakko-rail-on');
  }, [hideRail, isDesktop]);

  useEffect(() => {
    if (hideRail || isDesktop) {
      document.body.classList.remove('stakko-mobile-account');
      return;
    }
    document.body.classList.add('stakko-mobile-account');
    return () => document.body.classList.remove('stakko-mobile-account');
  }, [hideRail, isDesktop]);

  useEffect(() => {
    if (!view && !mobileOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (view) setView(null);
      else setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, mobileOpen]);

  useEffect(() => {
    return stakkoBus.on<string>(STAKKO_EVENTS.openPanel, (next) => {
      if (typeof next !== 'string') return;
      setView(next as StakkoPanelView);
      setMobileOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  const name = profile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'Přihlásit se';
  const photoURL = profile?.photoURL || user?.photoURL || null;
  const avatarName = user ? name : 'V';
  const avatarPhoto = user ? photoURL : null;
  const hasBadge = unreadNotifications + incomingRequests.length > 0;

  return (
    <div className={`stakko-root stakko-theme-${resolvedTheme}${className ? ` ${className}` : ''}`}>
      {children}

      {!hideRail && isDesktop ? (
        <>
          <StakkoRail view={view} onOpen={setView} primaryAction={primaryAction} />

          {view ? (
            <>
              <div className="stakko-panel-backdrop" onClick={() => setView(null)} />
              <section className="stakko-panel" aria-label={panelTitle(view)}>
                <header className="stakko-panel-header">
                  <h2>{panelTitle(view)}</h2>
                  <button
                    type="button"
                    className="stakko-icon-btn"
                    onClick={() => setView(null)}
                    aria-label="Zavřít panel"
                  >
                    <XIcon size={18} />
                  </button>
                </header>
                <StakkoPanelContent
                  view={view}
                  onNavigate={setView}
                  onClose={() => setView(null)}
                  onBuyCredits={onBuyCredits}
                />
              </section>
            </>
          ) : null}
        </>
      ) : null}

      {!hideRail && !isDesktop ? (
        <>
          <button
            type="button"
            className="stakko-mobile-trigger"
            onClick={() => setMobileOpen(true)}
            aria-label="Otevřít Stakko účet"
          >
            <StakkoAvatar name={avatarName} photoURL={avatarPhoto} size={32} />
            {hasBadge ? <span className="stakko-mobile-dot" /> : null}
          </button>

          {mobileOpen ? (
            <div className="stakko-sheet-root">
              <div className="stakko-sheet-backdrop" onClick={() => setMobileOpen(false)} />
              <section className="stakko-sheet" role="dialog" aria-modal="true" aria-label="Stakko">
                <div className="stakko-sheet-grab" />
                <header className="stakko-sheet-header">
                  {view ? (
                    <button
                      type="button"
                      className="stakko-icon-btn"
                      onClick={() => setView(null)}
                      aria-label="Zpět"
                    >
                      <ChevronRightIcon size={18} style={{ transform: 'rotate(180deg)' }} />
                    </button>
                  ) : (
                    <StakkoAvatar name={avatarName} photoURL={avatarPhoto} size={30} />
                  )}
                  <h2>{view ? panelTitle(view) : 'Stakko'}</h2>
                  <button
                    type="button"
                    className="stakko-icon-btn"
                    onClick={() => setMobileOpen(false)}
                    aria-label="Zavřít"
                  >
                    <XIcon size={18} />
                  </button>
                </header>

                {view ? (
                  <StakkoPanelContent
                    view={view}
                    onNavigate={setView}
                    onClose={() => setMobileOpen(false)}
                    onBuyCredits={onBuyCredits}
                  />
                ) : (
                  <MobileMenu onOpen={(next) => setView(next)} />
                )}
              </section>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
